import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  CANDLESTICK_COLORS,
  DEFAULT_COMPACT_THRESHOLD,
  formatPrice,
  formatVolume,
} from "~utils/ticker-chart";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";
import { isOhlcvPoint } from "~types/types";

export interface OhlcvOverlayProps {
  seriesId: string;
  locale?: string;
}

const OVERLAY_PADDING = 4;

function getDirectionColor(close: number, open: number): string {
  if (close > open) return CANDLESTICK_COLORS.up;
  if (close < open) return CANDLESTICK_COLORS.down;

  return CANDLESTICK_COLORS.unchanged;
}

function getVolumeDirectionColor(
  currentClose: number,
  previousClose?: number
): string {
  if (previousClose === undefined) return CANDLESTICK_COLORS.unchanged;
  if (currentClose > previousClose) return CANDLESTICK_COLORS.up;
  if (currentClose < previousClose) return CANDLESTICK_COLORS.down;

  return CANDLESTICK_COLORS.unchanged;
}

const OhlcvOverlayInner: React.FC<OhlcvOverlayProps> = ({
  seriesId,
  locale = "en-US",
}) => {
  const store = useChartContext();
  const storeRef = useStoreRef(store);

  const revision = useChartSubscription(store, "hover");

  const content = useMemo(() => {
    const currentStore = storeRef.current;
    const hover = currentStore.getHoverState();

    if (!hover.visible) return null;

    const point = hover.nearestPoints.get(seriesId);

    if (!point) return null;

    const state = currentStore.getState();
    const layout = currentStore.getLayout();
    const scales = currentStore.getScales();
    const viewport = currentStore.getViewport();
    const seriesMeta = state.seriesMeta.get(seriesId);
    const currency = seriesMeta?.issuedCurrency ?? "USD";

    if (point.timestamp < viewport.start || point.timestamp > viewport.end) {
      return null;
    }

    const [yMin, yMax] = scales.y.domain();
    const useCompact =
      Math.abs(yMax) >= DEFAULT_COMPACT_THRESHOLD ||
      Math.abs(yMin) >= DEFAULT_COMPACT_THRESHOLD;

    const formatOpts = { currency, locale, compact: useCompact };

    if (isOhlcvPoint(point)) {
      const color = getDirectionColor(point.close, point.open);

      return {
        type: "ohlcv" as const,
        open: formatPrice(point.open, formatOpts),
        high: formatPrice(point.high, formatOpts),
        low: formatPrice(point.low, formatOpts),
        close: formatPrice(point.close, formatOpts),
        volume:
          point.volume !== undefined
            ? formatVolume(point.volume, locale)
            : undefined,
        color,
        x: layout.priceArea.x + OVERLAY_PADDING,
        y: layout.priceArea.y + OVERLAY_PADDING,
        fontSize: layout.typography.axisLabelSize,
      };
    }

    if (point.volume !== undefined) {
      const visibleData = currentStore.getVisibleData(seriesId);
      const pointIndex = visibleData.findIndex(
        (p) => p.timestamp === point.timestamp
      );
      const previousPoint =
        pointIndex > 0 ? visibleData[pointIndex - 1] : undefined;
      const currentValue = "value" in point ? point.value : 0;

      let prevValue: number | undefined;

      if (previousPoint && "value" in previousPoint) {
        prevValue = previousPoint.value;
      } else if (previousPoint) {
        prevValue = 0;
      }
      const color = getVolumeDirectionColor(currentValue, prevValue);

      return {
        type: "volume" as const,
        volume: formatVolume(point.volume, locale),
        color,
        x: layout.priceArea.x + OVERLAY_PADDING,
        y: layout.priceArea.y + OVERLAY_PADDING,
        fontSize: layout.typography.axisLabelSize,
      };
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, locale]);

  if (!content) return null;

  const overlayWidth = content.type === "ohlcv" ? 400 : 120;
  const overlayHeight = content.fontSize + OVERLAY_PADDING * 2;

  return (
    <foreignObject
      x={content.x}
      y={content.y}
      width={overlayWidth}
      height={overlayHeight}
      pointerEvents="none"
      style={{ overflow: "visible" }}
      data-component="price-movement-chart-ohlcv-overlay"
    >
      <div
        className={styles.ohlcvOverlay}
        style={{ fontSize: `${content.fontSize}px` }}
      >
        {content.type === "ohlcv" ? (
          <>
            <span className={styles.ohlcvOverlayLabel}>O:</span>
            <span
              className={styles.ohlcvOverlayValue}
              style={{ color: content.color }}
            >
              {content.open}
            </span>
            <span className={styles.ohlcvOverlayLabel}>H:</span>
            <span
              className={styles.ohlcvOverlayValue}
              style={{ color: content.color }}
            >
              {content.high}
            </span>
            <span className={styles.ohlcvOverlayLabel}>L:</span>
            <span
              className={styles.ohlcvOverlayValue}
              style={{ color: content.color }}
            >
              {content.low}
            </span>
            <span className={styles.ohlcvOverlayLabel}>C:</span>
            <span
              className={styles.ohlcvOverlayValue}
              style={{ color: content.color }}
            >
              {content.close}
            </span>
            {content.volume !== undefined && (
              <>
                <span className={styles.ohlcvOverlayLabel}>V:</span>
                <span
                  className={styles.ohlcvOverlayValue}
                  style={{ color: content.color }}
                >
                  {content.volume}
                </span>
              </>
            )}
          </>
        ) : (
          <>
            <span className={styles.ohlcvOverlayLabel}>Vol:</span>
            <span
              className={styles.ohlcvOverlayValue}
              style={{ color: content.color }}
            >
              {content.volume}
            </span>
          </>
        )}
      </div>
    </foreignObject>
  );
};

export const OhlcvOverlay = React.memo(OhlcvOverlayInner);
OhlcvOverlay.displayName = "OhlcvOverlay";
